import React, { useEffect, useState } from "react";
import { ROUTES } from "../../../constants/routes";
import { usePayContext } from "../../../hooks/usePayContext";

import {
  ModalBody,
  ModalContent,
  PageContent,
} from "../../Common/Modal/styles";

import { WalletPaymentOption } from "@daimo/pay-common";
import { parseUnits } from "viem";
import styled from "../../../styles/styled";
import {
  formatUsd,
  roundTokenAmount,
  roundTokenAmountUnits,
  roundUsd,
  tokenAmountToRoundedUsd,
  USD_DECIMALS,
  usdToRoundedTokenAmount,
} from "../../../utils/format";
import { isValidNumber, sanitizeNumber } from "../../../utils/validateInput";
import Button from "../../Common/Button";
import SwitchButton from "../../Common/SwitchButton";
import TokenLogoSpinner from "../../Spinners/TokenLogoSpinner";
import AmountInputField from "./AmountInputField";

const MultiCurrencySelectAmount: React.FC<{
  selectedTokenOption: WalletPaymentOption;
  setSelectedTokenOption: (option: WalletPaymentOption) => void;
  nextPage: ROUTES;
}> = ({ selectedTokenOption, setSelectedTokenOption, nextPage }) => {
  const { paymentState, setRoute, triggerResize } = usePayContext();
  const maxUsdLimit = paymentState.getOrderUsdLimit();

  const balanceToken = selectedTokenOption.balance.token;

  // Balance messages for USD and token modes
  const isUsdStablecoin = balanceToken.fiatISO === "USD";
  const usdBalanceMessage = isUsdStablecoin
    ? `Balance: ${formatUsd(selectedTokenOption.balance.usd)}`
    : `Balance: ${formatUsd(selectedTokenOption.balance.usd)} ${balanceToken.symbol}`;
  const tokenBalanceMessage = `Balance: ${roundTokenAmount(selectedTokenOption.balance.amount, balanceToken)} ${balanceToken.symbol}`;

  const getBalanceMessage = (editingUsd: boolean) =>
    editingUsd ? usdBalanceMessage : tokenBalanceMessage;

  const [usdStr, setUsdValue] = useState("");
  const [tokenStr, setTokenValue] = useState("");
  const [isEditingUsd, setIsEditingUsd] = useState(true);
  const [message, setMessage] = useState<string | null>(usdBalanceMessage);
  const [continueDisabled, setContinueDisabled] = useState(true);

  useEffect(() => {
    triggerResize();
  }, [message]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Update the editable value and secondary value, taking into account whether
   * the user is currently editing the USD value or the token value.
   */
  const updateValues = (
    newUsdValue: string,
    newTokenValue: string,
    newIsEditingUsd: boolean,
  ) => {
    const sanitizedUsdValue = sanitizeNumber(newUsdValue);
    const sanitizedTokenValue = sanitizeNumber(newTokenValue);
    const usdNum = Number(sanitizedUsdValue);
    const tokenNum = Number(sanitizedTokenValue);

    // Update the state. Don't sanitize the value if the user is editing it.
    // Show empty for zero values so user can start typing immediately.
    // Strip trailing zeros from token values for cleaner display.
    const stripTrailingZeros = (val: string) =>
      val.includes(".") ? val.replace(/\.?0+$/, "") : val;

    setUsdValue(
      newIsEditingUsd ? newUsdValue : usdNum > 0 ? roundUsd(usdNum) : "",
    );
    setTokenValue(
      newIsEditingUsd
        ? tokenNum > 0
          ? stripTrailingZeros(roundTokenAmountUnits(tokenNum, balanceToken))
          : ""
        : newTokenValue,
    );
    setIsEditingUsd(newIsEditingUsd);

    setContinueDisabled(
      usdNum <= 0 ||
        usdNum < selectedTokenOption.minimumRequired.usd ||
        usdNum > selectedTokenOption.balance.usd ||
        usdNum > maxUsdLimit,
    );

    // Format amounts based on current mode (USD or token)
    const formatAmount = (usd: number) =>
      newIsEditingUsd
        ? formatUsd(usd)
        : `${usdToRoundedTokenAmount(usd, balanceToken)} ${balanceToken.symbol}`;

    if (usdNum > selectedTokenOption.balance.usd) {
      setMessage(
        `Amount exceeds your balance: ${formatAmount(selectedTokenOption.balance.usd)}`,
      );
    } else if (usdNum > maxUsdLimit) {
      setMessage(`Maximum ${formatAmount(maxUsdLimit)}`);
    } else if (usdNum > 0 && usdNum < selectedTokenOption.minimumRequired.usd) {
      setMessage(
        `Minimum ${formatAmount(selectedTokenOption.minimumRequired.usd)}`,
      );
    } else {
      setMessage(getBalanceMessage(newIsEditingUsd));
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maxDecimals = isEditingUsd
      ? USD_DECIMALS
      : balanceToken.displayDecimals;
    if (value !== "" && !isValidNumber(value, maxDecimals)) return;

    const sanitizedValue = sanitizeNumber(value);

    const newUsdValue = isEditingUsd
      ? value
      : tokenAmountToRoundedUsd(
          parseUnits(sanitizedValue, balanceToken.decimals),
          balanceToken,
        );
    const newTokenValue = isEditingUsd
      ? usdToRoundedTokenAmount(Number(sanitizedValue), balanceToken)
      : value;
    updateValues(newUsdValue, newTokenValue, isEditingUsd);
  };

  const handleMax = () => {
    const usdStr = roundUsd(Number(selectedTokenOption.balance.usd));
    const tokenStr = roundTokenAmount(
      selectedTokenOption.balance.amount,
      balanceToken,
    );
    updateValues(usdStr, tokenStr, isEditingUsd);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !continueDisabled) {
      handleContinue();
    }
  };

  const handleSwitchCurrency = () => {
    updateValues(usdStr, tokenStr, !isEditingUsd);
  };

  const handleContinue = () => {
    const usd = Number(sanitizeNumber(usdStr));
    const amountUnits = usd / balanceToken.usd;
    const amount = parseUnits(amountUnits.toString(), balanceToken.decimals);
    setSelectedTokenOption({
      ...selectedTokenOption,
      required: {
        token: balanceToken,
        amount: amount.toString() as `${bigint}`,
        usd,
      },
    });
    paymentState.setChosenUsd(usd);
    setRoute(nextPage, {
      amountUsd: usd,
      amountUnits,
      tokenSymbol: balanceToken.symbol,
    });
  };

  return (
    <PageContent>
      <TokenLogoSpinner token={balanceToken} />
      <ModalContent $preserveDisplay={true}>
        <AmountInputContainer>
          {/* Invisible div to balance spacing */}
          <MaxButton style={{ visibility: "hidden" }}>Max</MaxButton>
          <AmountInputField
            value={isEditingUsd ? usdStr : tokenStr}
            onChange={handleAmountChange}
            currency={isEditingUsd ? "$" : balanceToken.symbol}
            onKeyDown={handleKeyDown}
          />
          <MaxButton onClick={handleMax}>Max</MaxButton>
        </AmountInputContainer>

        {balanceToken.fiatISO !== "USD" && (
          <SwitchContainer>
            <SwitchButton onClick={handleSwitchCurrency}>
              <SecondaryAmount>
                {isEditingUsd
                  ? `${tokenStr || "0"} ${balanceToken.symbol}`
                  : `$${usdStr || roundUsd(0)}`}
              </SecondaryAmount>
            </SwitchButton>
          </SwitchContainer>
        )}

        {message && <ModalBody>{message}</ModalBody>}

        <Button onClick={handleContinue} disabled={continueDisabled}>
          Continue
        </Button>
      </ModalContent>
    </PageContent>
  );
};

const AmountInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const SecondaryAmount = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 21px;
  color: var(--ck-body-color-muted);
  strong {
    font-weight: 500;
    color: var(--ck-body-color);
  }
`;
const MaxButton = styled.button`
  display: inline-block;
  padding: 3px 8px;
  border-radius: var(--ck-primary-button-border-radius);
  font-size: 14px;
  font-weight: 400;
  background: var(
    --ck-secondary-button-background,
    var(--ck-body-background-secondary)
  );
  color: var(--ck-body-color-secondary);
  cursor: pointer;
`;
const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default MultiCurrencySelectAmount;
